import {
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewEncapsulation,
  inject
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ApiConstants } from 'src/app/base/api-constants';
import { clone, isEqual } from 'lodash-es';
import { AppGlobalService } from 'src/app/services/app.global.service';
import { UtilService } from 'src/app/services/util.service';
import { v4 as uuidv4 } from 'uuid';
import { HttpBaseService } from 'src/app/base/http-base.service';
import { ReferenceTreeViewComponent } from '../reference-tree-view/reference-tree-view.component';
import { DialogService } from 'primeng/dynamicdialog';
import { ReferenceUtilService } from '../reference-tree-view/reference-util.service';
import { ActionType, ModelType, UiActions } from '../reference-tree-view/reference.enum';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AppLoaderService } from 'src/app/services/app-loader.service';

import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import { ListPageActionService } from 'src/app/services/list-page-action.service';
import { ScreenIdentifierService } from 'src/app/services/screen-identifier.service';

@AutoUnsubscribe({ arrayName: 'subscriptions' })
@Component({
  selector: 'vb-conditional-formatter',
  templateUrl: './conditional-formatter.component.html',
  styleUrls: ['./conditional-formatter.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ConditionalFormatterComponent),
      multi: true
    },
    DialogService
  ],
  standalone: false  //NOSONAR
})
export class ConditionalFormatterComponent implements OnInit, OnDestroy {
  private readonly renderer = inject(Renderer2);
  private readonly elementRef = inject(ElementRef);
  private readonly appGlobalService = inject(AppGlobalService);
  private readonly util = inject(UtilService);
  private readonly bs = inject(HttpBaseService);
  private readonly referenceDialogService = inject(DialogService);
  private readonly referenceUtil = inject(ReferenceUtilService);
  private readonly translate = inject(TranslateService);
  private readonly loader = inject(AppLoaderService);
  private readonly confirmationDialogService = inject(ConfirmationDialogService);
  private readonly listPageActionService = inject(ListPageActionService);
  private readonly screenIdentifierService = inject(ScreenIdentifierService);

  @Input() element: any;
  @Input() node: any;
  @Input() fieldName: any;
  @Input() disableField = false;
  @Input() formData: any;
  @Input() skipReferenceCheck = false;
  @Input() isGenerationWizard = false;
  @Input() workflowAssociated = false;
  @Input() showFormattingButton = true;
  private modelValue: any;
  popupOpened = false;
  private onChange: (m: any) => void;
  private onTouched: (m: any) => void;
  disabled: boolean;
  subscriptions: Subscription[] = [];

  conditionType = 'None';
  conditions: any = [];
  showCustomFormatter = false;
  showReorderWindow = false;
  values: any = [];
  list: any = [];
  options = ['None', 'Auto', 'Custom'];
  predefinedColors: string[] = [
    '#E544FF',
    '#8220FF',
    '#1A73E8',
    '#16D3FD',
    '#00D7A3',
    '#9CD3AB',
    '#99CF00',
    '#E8D205',
    '#FCD48B',
    '#FF6B00',
    '#F6A09D',
    '#FF4DA2'
  ];
  appId: any;

  get isBetaAccessPrivilegeConsumable(): boolean {
    return this.appGlobalService.betaAccessPrivilegeForApp;
  }

  get model() {
    return this.modelValue;
  }

  writeValue(value: any): void {
    if (value) {
      let valueObj;
      if (typeof value === 'string') {
        valueObj = JSON.parse(value);
      } else {
        valueObj = value;
      }
      // Handle styleClass mode (showFormattingButton=false):
      if (!this.showFormattingButton) {
        if (Array.isArray(valueObj)) {
          this.values = valueObj.map((v: any) => {
            if (typeof v === 'string') {
              return { name: v, sid: uuidv4() };
            }
            return v;
          });
        } else {
          this.values = [];
        }
        // Store the actual array (not stringified) for styleClass mode
        this.modelValue = Array.isArray(valueObj) ? valueObj : [];
        return;
      }
      this.values = valueObj?.values || [];
      this.setSelectedFormatting({ ...valueObj?.conditions });
      //this.initConditions();
    } else {
      this.values = [];
      this.conditions = [];
      this.conditionType = 'None';
      this.initConditions();
    }
    this.modelValue = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  set(value: any) {
    this.modelValue = value;
    this.onChange(this.modelValue);
  }

  setDisabledState(isDisabled: boolean) {
    this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
    this.disabled = isDisabled;
  }

  setSelectedFormatting(value: any) {
    if (!value) {
      return;
    }
    if (value.conditionType) {
      this.conditionType = clone(value.conditionType);
    }
    if (value.conditions) {
      this.conditions = JSON.parse(JSON.stringify(value.conditions));
    }
  }

  adjust(color, opacity) {
    const _opacity = Math.round(Math.min(Math.max(opacity || 1, 0), 1) * 255);
    return color + _opacity.toString(16).toUpperCase();
  }

  initConditions() {
    const conditions: any[] = [];
    if (this.values?.length && this.conditionType !== 'None') {
      this.values.map((val: any, index: number) => {
        const existing = this.conditions?.find((c) => c.id === val.sid);
        if (this.conditionType === 'Custom' && existing && existing.style && existing.style['background-color']) {
          //Custom- with existing conditions
          conditions.push(existing);
        } else {
          //Auto and Custom(without existing conditions(initial))
          const colorIndex = index % this.predefinedColors.length;
          conditions.push({
            id: val.sid,
            query: { condition: 'and', rules: [{ field: `${this.fieldName}`, operator: '==', value: `${val.name}` }] },
            style: {
              'background-color': this.adjust(this.predefinedColors[colorIndex], 0.2),
              color: this.predefinedColors[colorIndex],
              'cell-background-color': '#ffffff',
              'text-align': 'center',
              showText: true,
              icon: null,
              iconColor: '#333333'
            }
          });
        }
      });
      this.conditions = [...conditions];
    }
  }

  handleFormatterModalClose() {
    // StyleClass mode: simple close with optional dirty check
    if (!this.showFormattingButton) {
      const currentValues = this.values.map((v: any) => v.name || v);
      let isDirtyStyleClass = false;
      if (this.modelValue) {
        // modelValue is now an array for styleClass mode, compare directly
        const oldVal = Array.isArray(this.modelValue) ? this.modelValue : [];
        isDirtyStyleClass = !isEqual(oldVal, currentValues);
      } else {
        isDirtyStyleClass = currentValues.length > 0;
      }
      if (isDirtyStyleClass) {
        this.confirmationDialogService.confirm({
          message: this.translate.instant('UNSAVED_CHANGES_CONFIRM'),
          header: 'Confirmation',
          accept: () => {
            this.showReorderWindow = false;
            this.saveFormatting();
          },
          reject: () => {
            this.showReorderWindow = false;
          }
        });
      } else {
        this.showReorderWindow = false;
      }
      return;
    }
    // Conditional formatter mode: existing logic
    const obj = {
      conditionType: this.conditionType,
      conditions: this.conditions
    };
    const valueObj = { values: this.values, conditions: obj };
    let isDirty = false;
    if (this.modelValue) {
      const oldVal = JSON.parse(this.modelValue);
      isDirty = !isEqual(oldVal, valueObj);
    } else {
      isDirty = true;
    }
    if (this.disableField) {
      isDirty = false; // If field is disabled, ignore changes and don't show confirmation
    }
    if (isDirty) {
      this.confirmationDialogService.confirm({
        message: this.translate.instant('UNSAVED_CHANGES_CONFIRM'),
        header: 'Confirmation',
        accept: () => {
          this.showCustomFormatter = false;
          this.showReorderWindow = false;
          this.saveFormatting();
        }
      });
    } else {
      this.showCustomFormatter = false;
      this.showReorderWindow = false;
    }
  }

  preCheck(event: any) {
    // StyleClass mode: skip reference checking, save directly
    if (!this.showFormattingButton) {
      this.saveFormatting();
      return;
    }
    this.loader.show();
    const data = JSON.parse(this.modelValue);
    let reponsevalues: any = [];
    const modifiedValues: any = [];
    data.values?.map((o: any) => {
      this.values?.map((p: any) => {
        if (p.name !== o.name && o.sid === p.sid) {
          modifiedValues.push(o.sid);
        }
      });
    });
    if (modifiedValues.length > 0) {
      const referencePayload = {
        modelType: ModelType.FIELD_VALUE,
        actionType: ActionType.FIELD_VALUE_DELETE,
        action: UiActions.DELETE
      };
      const referencePromises = modifiedValues.map((sid: string) =>
        this.referenceUtil.getReferences({ modelId: sid, ...referencePayload })
      );
      referencePromises.push(
        this.referenceUtil.getReferences({ modelId: this.formData.sid, ...referencePayload })
      );
      Promise.all(referencePromises)
        .then((results: any) => {
          this.loader.hide();
          reponsevalues = results?.flat();
          if (reponsevalues.length > 0) {
            const d = {
              genericAction: 'UPDATE',
              propertyAction: 'ALLOWED_VALUES_RENAME'
            };
            if (this.popupOpened) {
              return;
            }
            this.util.setPopupRestriction(this.popupOpened);
            this.showReferenceTree(event, reponsevalues, d);
          } else {
            this.saveFormatting();
          }
        })
        .catch((err) => {
          this.loader.hide();
        });
    } else {
      this.saveFormatting();
    }
  }

  saveFormatting() {
    this.loader.hide();
    // StyleClass mode (showFormattingButton=false): save as actual array ['class1', 'class2']
    if (!this.showFormattingButton) {
      const valueArr = this.values.map((v: any) => v.name || v);
      // Emit the actual array, NOT a stringified version
      // This ensures styleClass is saved as ["back"] not "[\"back\"]"
      const oldValStr = JSON.stringify(this.modelValue || []);
      const newValStr = JSON.stringify(valueArr);
      if (oldValStr !== newValStr) {
        this.modelValue = valueArr;
        this.onChange(valueArr);  // Emit array directly
      }
      if (this.showReorderWindow) {
        this.showReorderWindow = false;
      }
      return;
    }
    const obj = {
      conditionType: this.conditionType,
      conditions: this.conditions
    };
    const valueObj = { values: this.values, conditions: obj };
    if (this.modelValue) {
      const oldVal = JSON.parse(this.modelValue);
      if (isEqual(oldVal, valueObj)) {
        return;
      }
    }
    this.modelValue = JSON.stringify(valueObj);
    if (this.showCustomFormatter) {
      this.showCustomFormatter = !this.showCustomFormatter;
    }
    this.onChange(this.modelValue);
  }

  getImgSrc(icon) {
    const file = icon?.icon?.[0]?.id;
    const api = `/rest/v1/${ApiConstants.getFileContents.url}${file}`;
  }

  getImage(icon) {
    const file = icon?.icon?.[0]?.id;
    const api = `/rest/v1/${ApiConstants.getFileContents.url}${file}/${this.appId}`;
    return api;
  }

  handleTypeChange(e: any) {
    this.showCustomFormatter = e.value === 'Custom';
  }

  ngOnInit(): void {
    this.appId = this.appGlobalService.get('appId');
    // Only override disableField if not already disabled by parent
    if (!this.disableField && this.formData.f_sysF && this.formData.name === 'Language Code') {
      this.disableField = true;
    }
  }

  onAdd(event, value) {
    if (value) {
      console.log(value);
    }
  }

  toggleFormatter(event?: any) {
    if (event) {
      this.initConditions();
    }
    this.showCustomFormatter = !this.showCustomFormatter;
  }

  toggleReorderWindow(event?: any) {
    this.showReorderWindow = !this.showReorderWindow;
    this.list = [...this.values];
  }

  editAllowedVal(val) {
    val = { ...val, ...{ edit: true } };
    if (!val.newName) {
      val = { ...val, ...{ newName: val.name } };
    }
    let items = [...this.list];
    items = items.map((item) => {
      return item.sid === val.sid ? val : item;
    });
    this.list = [...items];
  }

  saveAllowedVal(val) {
    // If value is cleared, remove the entry from the list
    if (!val.newName || val.newName.trim() === '') {
      this.list = this.list.filter((item) => item.sid !== val.sid);
      return;
    }
    // StyleClass mode: validate format
    if (!this.showFormattingButton) {
      if (!this.screenIdentifierService.isValidStyleClassFormat(val.newName)) {
        val.err = true;
        this.util.showMessage('error', this.translate.instant('INVALID_STYLE_CLASS_FORMAT'));
        return;
      }
    }
    const dupAltNme = this.list.find(
      (aVal) => aVal.sid !== val.sid && aVal.name && aVal.name.toLowerCase() === val.newName.toLowerCase()
    );
    if (dupAltNme) {
      val.err = true;
      this.util.showMessage('error', !this.showFormattingButton ? this.translate.instant('DUPLICATE_STYLE_CLASS', { styleclass: val.newName }) : `Duplicate Allowed Value name`);
      return;
    }
    val.err = false;
    val.edit = false;
    val.name = val.newName;

    let items = [...this.list];
    items = items.map((item) => {
      return item.sid === val.sid ? val : item;
    });
    this.list = [...items];
  }

  cancelEdit(val) {
    val.edit = false;
    delete val.newName;
    delete val.err;
    let items = [...this.list];
    items = items.map((item) => {
      return item.sid === val.sid ? val : item;
    });
    this.list = [...items];
  }

  handleOptionsClick(e) {
    if (this.conditionType === 'Auto') {
      this.initConditions();
    } else if (this.conditionType === 'Custom') {
      this.initConditions();
    } else if (this.conditionType === 'None') {
      this.conditions = [];
    }
  }

  onChangeValue(event, isDelete?: boolean) {
    const isAllowedValuesHasSpecificSpecialChar = false;

    if (!isDelete) {
      const success = this.handleAddValue(event);
      if (!success) {
        return;
      }
      if (!isAllowedValuesHasSpecificSpecialChar) {
        this.saveFormatting();
      }
      return;
    }

    // Handle delete
    if (this.skipReferenceCheck && !this.showFormattingButton) {
      this.saveFormatting();
      return;
    }
    this.handleDeleteWithReferences(event);
  }

  private handleAddValue(event): boolean {
    try {
      // StyleClass mode: validate format
      if (!this.showFormattingButton && !this.screenIdentifierService.isValidStyleClassFormat(event.value)) {
        this.util.showMessage('error', this.translate.instant('INVALID_STYLE_CLASS_FORMAT'));
        this.values = [...this.values.filter((v) => v?.name)];
        return false;
      }

      const dupAltNme = this.values.find((v) => event?.value.toLowerCase() === v?.name?.toLowerCase());
      if (dupAltNme) {
        const errorMsg = !this.showFormattingButton
          ? this.translate.instant('DUPLICATE_STYLE_CLASS', { styleclass: event.value })
          : `Duplicate Allowed Value name`;
        this.util.showMessage('error', errorMsg);
        this.values = [...this.values.filter((v) => v?.name)];
        return false;
      }

      const findIndex = this.values.findIndex((e) => e === event.value);
      this.values[findIndex] = { sid: uuidv4(), name: event.value.trim() };
      const items = this.values.filter((item) => item?.name?.toLowerCase() === event.value.toLowerCase().trim());
      if (items.length > 1) {
        this.values.splice(findIndex, 1);
      }
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  private handleDeleteWithReferences(event): void {
    this.loader.show();
    const deletePayload = {
      modelType: ModelType.FIELD_VALUE,
      actionType: ActionType.FIELD_VALUE_DELETE,
      action: UiActions.DELETE
    };

    Promise.all([
      this.referenceUtil.getReferences({ modelId: event?.value?.sid, ...deletePayload }),
      this.referenceUtil.getReferences({ modelId: this.formData?.sid, ...deletePayload })
    ])
      .then((value: any) => this.processDeleteReferences(value, event))
      .catch((error) => console.error(error));
  }

  private processDeleteReferences(value: any, event): void {
    const data = JSON.parse(this.modelValue);
    const findIndex = data?.values?.findIndex((e) => e.sid === event?.value?.sid);

    // Extract references - preserving original logic
    let value0: any[] = [];
    const value1: any[] = [];
    if (Array.isArray(value[0])) {
      value0 = value[0];
    } else if (value[0] && Array.isArray(value[0].referenceList)) {
      value0 = value[0].referenceList;
    }
    if (Array.isArray(value[1])) {
      value0 = value[1];
    } else if (value[1] && Array.isArray(value[1].referenceList)) {
      value0 = value[1].referenceList;
    }

    const refs = [...value0, ...value1];

    if (refs.length === 0) {
      this.saveFormatting();
      return;
    }

    const node = { name: this.formData?.name, type: 'Field' };
    this.referenceUtil.openReferenceTree(node, refs, UiActions.DELETE)
      .then((confirmed) => {
        if (confirmed) {
          this.saveFormatting();
        } else {
          this.values.splice(findIndex, 0, event.value);
          this.values = [...this.values];
          this.loader.hide();
        }
      })
      .catch(() => {
        this.loader.hide();
      });
  }

  onReorder(event) {
    // this.saveList(true);
  }

  saveList(event, fromReOrder?: boolean) {
    this.values = this.list.map((val) => {
      return { name: val.name, sid: val.sid };
    });
    if (!fromReOrder) {
      if (this.showReorderWindow) {
        this.showReorderWindow = !this.showReorderWindow;
      }
    }
    this.preCheck(event);
    // this.saveFormatting();
  }

  getReferences(id: string) {
    const promise = new Promise<any>((resolve, reject) => {
      const ref$ = this.bs.get(ApiConstants.getReferences, { modelId: id }).subscribe({
        next: (res: any) => {
          resolve(res);
        },
        error: (err: any) => {
          reject(err);
        },
        complete: () => {
          console.log('complete');
        }
      });
      this.subscriptions.push(ref$);
    });
    return promise;
  }

  showReferenceTree(nodedata: any, value: any, rawData: any = {}) {
    const modalRef = this.referenceDialogService.open(ReferenceTreeViewComponent, {
      showHeader: false,
      styleClass: 'reference-tree',
      data: {
        node: { name: this.formData.name, type: 'Field' },
        referenceList: value,
        action: rawData.genericAction,
        propertyAction: rawData.propertyAction
      }
    });
    this.loader.hide();
    modalRef.onClose.subscribe((data: any) => {
      if (data.confirm) {
        this.saveFormatting();
      } else if (rawData.propertyAction === 'ALLOWED_VALUES_RENAME') {
        this.values = JSON.parse(this.modelValue).values;
      } else if (rawData.genericAction === 'WHERE_USED') {
        this.values.splice(rawData.index, 0, nodedata.value);
        this.values = [...this.values];
      }
    });
  }

  stopPropagation(event) {
    event.stopPropagation();
  }

  isOverflow(el: HTMLElement): boolean {
    return !!el && el.scrollWidth > el.clientWidth;
  }

  removeChip(item: any) {
    if (this.skipReferenceCheck) {
      this.confirmationDialogService.confirm({
        message: (this.isGenerationWizard && this.workflowAssociated ? this.translate.instant('WORKFLOW_STATUS_DELETE_CONFIRMATION') : "") + this.translate.instant('ALLOWED_VALUE_DELETE_CONFIRMATION', { value: item.name }),
        header: 'Confirmation',
        accept: () => {
          this.values = this.values.filter((v: any) => v !== item);
          this.saveFormatting();
        }
      });
      return;
    }

    // StyleClass mode: skip reference checking, just confirm and delete
    if (!this.showFormattingButton) {
      this.confirmationDialogService.confirm({
        message: this.translate.instant('ALLOWED_VALUE_DELETE_CONFIRMATION', { value: item.name }),
        header: 'Confirmation',
        accept: () => {
          this.values = this.values.filter((v: any) => v !== item);
          this.saveFormatting();
        }
      });
      return;
    }
    this.loader.show();
    const allowedValueObj = {
      modelId: item.sid,
      modelType: ModelType.FIELD_VALUE,
      actionType: ActionType.FIELD_VALUE_DELETE,
      action: UiActions.DELETE
    };

    this.referenceUtil.handleWithReferences({
      payload: allowedValueObj,
      node: { name: item.name || item, type: 'Field' },
      onConfirm: () => {
        this.confirmChipDeletion(item, true);
      },
      onNoReferences: () => {
        this.confirmChipDeletion(item);
      },
      onCancel: () => {
        this.loader.hide();
      },
      onError: () => {
        this.confirmChipDeletion(item);
      }
    });
  }

  confirmChipDeletion(item: any, fromReferencePopup?: boolean) {
    this.loader.hide();
    const itemName = item.name || item;

    if (!fromReferencePopup) {
          this.confirmationDialogService.confirm({
        message: this.translate.instant('ALLOWED_VALUE_DELETE_CONFIRMATION', { value: itemName }),
            header: 'Confirmation',
            accept: () => {
          this.performChipDeletion(item);
        },
        reject: () => {
          return;
            }
          });
    } else {
      this.performChipDeletion(item);
        }
  }

  performChipDeletion(item: any) {
    this.values = this.values.filter((v: any) => v !== item && v.sid !== item.sid);
    this.saveFormatting();
  }

  showWhereUsed(event: Event, chipText: string): void {
    event?.stopPropagation();
    // StyleClass mode: where-used not applicable
    if (!this.showFormattingButton) {
      return;
    }
    const chipValue = this.values.find((v: any) => v?.name === chipText || v === chipText);

    if (!chipValue || !chipValue.sid) {
      return;
    }

    this.loader.show();
    this.listPageActionService.showWhereUsed(
      {
        id: chipValue.sid,
        name: chipValue.name,
        type: this.translate.instant('OPTION'),
        apiName: 'getAllReferences',
        idParamName: 'modelId'
      },
      this.referenceDialogService,
      { hideLoader: () => this.loader.hide() }
    );
  }

  ngOnDestroy(): void {
    //AutoUnsubscribe
    if ($(this.elementRef.nativeElement)) {
      $(this.elementRef.nativeElement).remove();
    }
  }
}

